import React, { ReactElement, useEffect, useRef, useState } from 'react'

import { keyframes } from '@mui/material'

import { EAnalysisStatus } from 'types/Enums'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { useAnalysisDetail } from 'redux/hooks/Analyses'
import { useDashboardGroup } from 'redux/hooks/DashboardGroups'
import { useAnalysisRerun } from 'redux/hooks/AnalysisGenerations'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTasks } from 'redux/hooks/Tasks'
import { useHistory, useLocation } from 'react-router-dom'
import { useIsMobile, useParams } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Collapse from 'components_new/atoms/Collapse'
import Hero from 'components_new/molecules/Hero'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import ContentBox from 'components_new/molecules/ContentBox'
import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import IconButton from 'components_new/molecules/IconButton'
import LoadingSection from 'components_new/molecules/LoadingSection'
import NavigationItem from 'components_new/molecules/NavigationItem'
import ValueText from 'components_new/molecules/ValueText'

import ConfirmDialog from 'components_new/organisms/dialogs/ConfirmDialog'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import CreateTasksFromAnalysisDialog from 'components_new/organisms/dialogs/CreateTasksFromAnalysisDialog'
import AnalysisActivity from 'components_new/organisms/analyses/AnalysisActivity'
import AnalysisExcluded from 'components_new/organisms/analyses/AnalysisExcluded'
import AnalysisRerunning from 'components_new/organisms/analyses/AnalysisRerunning'
import AnalysisVersionFacade from 'components_new/organisms/analyses/AnalysisVersionFacade'
import SelectUser from 'components_new/organisms/SelectUser'
import TasksTable from 'components_new/organisms/tasks/TasksTable'

import AnalysisTemplate from 'components_new/templates/AnalysisTemplate'

import { TOPBAR_HEIGHT } from 'components_new/templates/PageLayout'
import { getInvestigationDashboardLocation } from 'utils/analysis'
import { getParsedDate } from 'utils/dateParser'
import { getContext } from 'utils/userEvents'
import * as UserEvents from 'redux/api/internal/UserEvents'
import { Action } from 'types/GlobalUserEvents'

// The rerun result taking the place of the run state.
const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`

const Analysis = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { t, translateEnum } = useTranslation()

  const { analysis, error, loading } = useAnalysisDetail(id)
  const { search } = useLocation()
  const { tryDeleteAnalysis, tryUpdateAnalysis } = useAnalysisActions()
  const { isAdmin } = useAccountSettings()
  const { tasks } = useTasks()
  const investigationDashboard = useDashboardGroup(
    analysis?.analysisType?.dashboard_group_id ?? null
  )
  const investigationDashboardId =
    investigationDashboard?.firstDashboardId ?? null

  const isMobile = useIsMobile()
  const [rightSidebarCollapsed, setRightSidebarCollapsed] =
    useState<boolean>(isMobile)

  const [dialog, setDialog] = useState<{
    open: boolean
    variant: 'undo' | 'delete' | 'reject'
  }>({
    open: false,
    variant: 'undo'
  })

  const [createTasks, setCreateTasks] = useState(false)
  const [tasksCollapsed, setTasksCollapsed] = useState<boolean | undefined>(
    undefined
  )
  const tasksRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (id) {
      UserEvents.log({
        action: Action.INSIGHT_OPENED,
        context: getContext(new URLSearchParams(search)),
        analysis_id: id
      })
    }
  }, [id])

  // While it is rerun, the run state takes its place and nothing changes it.
  const rerun = useAnalysisRerun(analysis)
  const [rerunDone, setRerunDone] = useState(false)
  const wasRerunning = useRef(false)

  useEffect(() => {
    if (wasRerunning.current && !rerun.running) {
      setRerunDone(true)
    }

    wasRerunning.current = rerun.running
  }, [rerun.running])

  if (error || (!loading && !analysis)) {
    return (
      <AnalysisTemplate>
        <ErrorPlaceholder
          actions={
            <Button
              onClick={() => history.push('/analyses')}
              startIcon={<Icon name="ArrowBackOutlined" />}
              variant="text"
            >
              {t('analyses', 'analyses')}
            </Button>
          }
          body={error ? undefined : t('analyses', 'error_not_found_body')}
          code={error ? 500 : 404}
        />
      </AnalysisTemplate>
    )
  }

  if (!analysis) {
    return (
      <AnalysisTemplate>
        <LoadingSection />
      </AnalysisTemplate>
    )
  }

  const { status } = analysis

  const analysisTasks = tasks.filter(
    (task) => task.source_type === 'ANALYSIS' && task.source_id === id
  )

  const handleUndo = (callback: () => void) => {
    tryUpdateAnalysis(
      analysis.id,
      { status: EAnalysisStatus.UNHANDLED },
      () => {
        callback()
        handleCloseDialog()
      }
    )
  }

  const handleConfirmUndo = () => {
    setDialog({ open: true, variant: 'undo' })
  }

  const handleReject = (callback: () => void) => {
    tryUpdateAnalysis(analysis.id, { status: EAnalysisStatus.REJECTED }, () => {
      callback()
      handleCloseDialog()
    })
  }

  const handleConfirmReject = () => {
    setDialog({ open: true, variant: 'reject' })
  }

  const handleTasksCreated = () => {
    setCreateTasks(false)
    setTasksCollapsed(false)
    requestAnimationFrame(() =>
      tasksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    )
  }

  // Disabled while rerun, with why; the span lets the tooltip show.
  const blockedWhileRerun = (control: ReactElement) =>
    rerun.running ? (
      <Tooltip title={t('analyses', 'rerun_disabled_tooltip')}>
        <span>{control}</span>
      </Tooltip>
    ) : (
      control
    )

  const handleCloseDialog = () => {
    setDialog((prevState) => ({
      ...prevState,
      open: false
    }))
  }

  return (
    <>
      <AnalysisTemplate
        content={{
          bgcolor: isMobile ? undefined : 'background.default',
          size: 'large'
        }}
        rightSidebar={{
          collapsed: rightSidebarCollapsed,
          setCollapsed: setRightSidebarCollapsed,
          content: (
            <>
              <AnalysisActivity id={id} />
            </>
          )
        }}
        topbar={{
          options:
            isAdmin && !rerun.running
              ? [
                  {
                    iconName: 'Delete',
                    onClick: () => setDialog({ open: true, variant: 'delete' }),
                    title: t('global', 'delete')
                  }
                ]
              : undefined,
          actions:
            isMobile || status === EAnalysisStatus.ACCEPTED ? undefined : (
              <>
                {blockedWhileRerun(
                  <Button
                    disabled={
                      status === EAnalysisStatus.REJECTED || rerun.running
                    }
                    onClick={
                      status === EAnalysisStatus.UNHANDLED
                        ? handleConfirmReject
                        : undefined
                    }
                    size="medium"
                    variant="outlined"
                  >
                    {status === EAnalysisStatus.REJECTED
                      ? translateEnum('analysis_status', status)
                      : t('analyses', 'reject_submit')}
                  </Button>
                )}
                {blockedWhileRerun(
                  <Button
                    disabled={rerun.running}
                    onClick={
                      status === EAnalysisStatus.UNHANDLED
                        ? () => setCreateTasks(true)
                        : handleConfirmUndo
                    }
                    size="medium"
                    startIcon={
                      status === EAnalysisStatus.REJECTED ? (
                        <Icon name="Undo" />
                      ) : undefined
                    }
                    variant={
                      status === EAnalysisStatus.UNHANDLED
                        ? 'contained'
                        : 'text'
                    }
                  >
                    {status === EAnalysisStatus.UNHANDLED
                      ? t('tasks', 'create_from_analysis_title')
                      : t('global', 'undo')}
                  </Button>
                )}
              </>
            )
        }}
      >
        <ContentBox
          border={true}
          collapse={{
            active: true,
            collapsed: tasksCollapsed,
            defaultCollapsed: analysisTasks.length === 0,
            onCollapse: () => setTasksCollapsed(true),
            onExpand: () => setTasksCollapsed(false)
          }}
          header={{
            actions:
              analysis.status === EAnalysisStatus.UNHANDLED
                ? blockedWhileRerun(
                  <IconButton
                    disabled={rerun.running}
                    name="Add"
                    onClick={() => setCreateTasks(true)}
                    size="small"
                    title={
                      rerun.running
                        ? undefined
                        : `${t('global', 'add')} ${t('tasks', 'task', true)}`
                    }
                    variant="outlined"
                  />
                )
                : null,
            size: 'medium',
            title: t('global', 'actions'),
            titleAdornment: <Chip label={analysisTasks.length} size="small" />
          }}
          ref={tasksRef}
          sx={{ mb: 2, scrollMarginTop: `${TOPBAR_HEIGHT + 16}px` }}
        >
          <TasksTable items={analysisTasks} maxHeight={400} />
        </ContentBox>

        <Box
          sx={
            isMobile
              ? undefined
              : {
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 12,
                  p: 4
                }
          }
        >
          {rerun.running ? (
            <AnalysisRerunning analysis={analysis} />
          ) : (
            <Box
              sx={
                rerunDone
                  ? {
                      animation: `${fadeUp} 320ms cubic-bezier(0.22, 1, 0.36, 1)`
                    }
                  : undefined
              }
            >
              <Hero
                background={true}
                chip={{
                  color: analysis.is_handled ? undefined : 'primary',
                  label: translateEnum('analysis_status', analysis.status)
                }}
                icon={{ name: 'Analysis' }}
                metaItems={[
                  {
                    content: getParsedDate(analysis.created_at, undefined, {
                      time: 'full'
                    }),
                    label: t('global', 'generated')
                  },
                  ...(analysis.decided_at
                    ? [
                        {
                          content: getParsedDate(analysis.decided_at),
                          label: t('global', 'handled')
                        }
                      ]
                    : []),
                  {
                    content: (
                      <SelectUser
                        onChange={(value) =>
                          tryUpdateAnalysis(analysis.id, {
                            responsible_account_id: value
                          })
                        }
                        size="small"
                        value={analysis.responsible_account_id}
                      />
                    ),
                    label: t('global', 'owner')
                  },
                  {
                    content: (
                      <ValueText
                        color="success.text"
                        displayPositiveIndicator={true}
                        unit={'kr/år'}
                        value={analysis.potential}
                      />
                    ),
                    label: t('analyses', 'potential')
                  },
                  {
                    content: !analysis.analysis_type_id ? (
                      <Tooltip title={t('analyses', 'retired_description')}>
                        <Text color="text.secondary">
                          <em>{t('analyses', 'retired')}</em>
                        </Text>
                      </Tooltip>
                    ) : (
                      <NavigationItem
                        arrow={true}
                        border={true}
                        color="primary"
                        item={{
                          href: `/analysis-types/${analysis.analysis_type_id}`,
                          icon: { name: 'AnalysisType' },
                          title: analysis.analysisType?.name ?? ''
                        }}
                      />
                    ),
                    label: t('analyses', 'generated_from')
                  },
                  // Hidden when unlinked or the user cannot see the dashboard.
                  ...(investigationDashboard && investigationDashboardId
                    ? [
                        {
                          content: (
                            <NavigationItem
                              arrow={true}
                              border={true}
                              color="primary"
                              item={{
                                icon: { name: 'Dashboards' },
                                onClick: () =>
                                  history.push(
                                    getInvestigationDashboardLocation(
                                      investigationDashboardId,
                                      analysis
                                    )
                                  ),
                                title: investigationDashboard.title
                              }}
                            />
                          ),
                          label: t('analyses', 'investigated_in')
                        }
                      ]
                    : []),
                  // Only when the run left something out.
                  ...(analysis.meta?.excluded_real_estates?.length ||
                  analysis.meta?.ambiguous_real_estates?.length
                    ? [
                        {
                          content: <AnalysisExcluded analysis={analysis} />,
                          label: t('analyses', 'excluded_label'),
                          tooltip: (
                            <>
                              <u>{t('analysis_types', 'excluded_label')}</u>{' '}
                              {t('analysis_types', 'excluded_help_dialog')}
                            </>
                          )
                        }
                      ]
                    : [])
                ]}
                size="medium"
                sx={{ mb: 2 }}
                title={analysis.title}
              />

              <Box sx={{ maxWidth: 'content', mb: 2 }}>
                <Text>{analysis.description}</Text>
              </Box>

              <AnalysisVersionFacade id={id} />
            </Box>
          )}
        </Box>

        {!isMobile ? (
          <Collapse
            in={rightSidebarCollapsed}
            unmountOnExit={true}
            sx={{ mt: 4 }}
          >
            <AnalysisActivity id={id} />
          </Collapse>
        ) : null}
      </AnalysisTemplate>

      {/*-- dialogs --*/}
      <CreateTasksFromAnalysisDialog
        analysis={analysis}
        onClose={() => setCreateTasks(false)}
        onCreate={handleTasksCreated}
        open={createTasks}
      />

      <ConfirmDialog
        action="reject"
        description={t('analyses', 'reject_confirm_description')}
        onClose={handleCloseDialog}
        onConfirm={handleReject}
        open={dialog.open && dialog.variant === 'reject'}
      />

      <ConfirmDialog
        action="undo"
        description={t('analyses', 'undo_confirm_description')}
        onClose={handleCloseDialog}
        onConfirm={handleUndo}
        open={dialog.open && dialog.variant === 'undo'}
        title={t('global', 'reject')}
      />

      <DeleteDialog
        onClose={handleCloseDialog}
        onDelete={(callback) =>
          tryDeleteAnalysis(analysis.id, (err) => {
            callback()

            if (!err) {
              handleCloseDialog()
              history.push('/analyses')
            }
          })
        }
        open={dialog.open && dialog.variant === 'delete'}
        title={analysis.title}
        type={t('analyses', 'analysis', true)}
      />
    </>
  )
}

export default Analysis
